/** Menu Actions — porté en fixed, comme les clients. */
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ActionChevron } from "../../components/ActionChevron";
import { placeFixedMenu, watchMenuPlace } from "../../components/placeMenu";
import "../clients/clientActions.css";
import { isActive } from "./format";
import type { Product } from "./types";

type CatalogActionsProps = {
  product: Product;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onToggleActive: () => void;
  onPurge?: () => void;
  onCreateQuote?: () => void;
  onCreateInvoice?: () => void;
};

const MENU_W = 200;
const MENU_H = 300;

export function CatalogActions({
  product,
  open,
  onToggle,
  onClose,
  onEdit,
  onDuplicate,
  onToggleActive,
  onPurge,
  onCreateQuote,
  onCreateInvoice,
}: CatalogActionsProps) {
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 360 });

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = box.current?.querySelector("button")?.getBoundingClientRect();
      if (!r) return;
      setPos(placeFixedMenu(r, MENU_W, menu.current?.scrollHeight || MENU_H));
    };
    const stop = watchMenuPlace(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menu.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      stop();
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose]);

  return (
    <div className="fx-actions" ref={box}>
      <button type="button" className="fx-actions__btn" aria-expanded={open} onClick={onToggle}>
        Actions <ActionChevron />
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className="fx-actions__menu"
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto" }}
            >
              <button type="button" className="fx-actions__item" role="menuitem" onClick={onEdit}>
                Éditer
              </button>
              <button
                type="button"
                className="fx-actions__item"
                role="menuitem"
                disabled={!onCreateQuote || !isActive(product)}
                title={isActive(product) ? undefined : "Réactive le produit pour l’ajouter à un devis"}
                onClick={onCreateQuote}
              >
                Créer un devis
              </button>
              <button
                type="button"
                className="fx-actions__item"
                role="menuitem"
                disabled={!onCreateInvoice || !isActive(product)}
                title={isActive(product) ? undefined : "Réactive le produit pour l’ajouter à une facture"}
                onClick={onCreateInvoice}
              >
                Créer une facture
              </button>
              <button type="button" className="fx-actions__item" role="menuitem" onClick={onDuplicate}>
                Dupliquer
              </button>
              <button
                type="button"
                className={`fx-actions__item${isActive(product) ? " fx-actions__item--danger" : ""}`}
                role="menuitem"
                onClick={onToggleActive}
              >
                {isActive(product) ? "Désactiver" : "Réactiver"}
              </button>
              {!isActive(product) && onPurge ? (
                <button
                  type="button"
                  className="fx-actions__item fx-actions__item--danger"
                  role="menuitem"
                  onClick={onPurge}
                >
                  Supprimer définitivement
                </button>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
